import { Injectable } from "@angular/core";
import { Entity, BuilderApplyItem } from "modeler/designer";
import { MathCalculator } from "modeler/math-calculator";
import { CatalogMaterial, CatalogService } from "./catalog.service";
import { FilesService, decodeBase64 } from "./files.service";
import { BuilderDesigner } from "modeler/builder-designer";
import { HttpClient } from "@angular/common/http";
import { EstimateService, PriceElement } from "app/planner/estimate";
import { FloorBuilder } from "modeler/floorplanner";
import { distance, Vector } from "modeler/geometry";
import { WebDesigner } from "modeler/webdesigner";

export enum FieldType {
  Number = "number",
  ModelsList = "models-list",
  MaterialsList = "materials-list",
}

interface BaseField {
  id: number;
  name?: string;
  type: FieldType;
  modelType?: string;
}

interface NumberField extends BaseField {
  min?: number;
  max?: number;
  step?: number;
  paramChanged: string;
  value: number;
}

interface ModelsListValue {
  name: string;
  modelUid?: string;
}

interface ModelsListField extends BaseField {
  value?: string;
  allValues?: ModelsListValue[];
  modelUid?: string;
}

interface MaterialsListValue {
  name: string;
  materialUid: string;
}

interface MaterialsListField extends BaseField {
  value: string;
  allValues: MaterialsListValue[];
}

export type FieldsData = (NumberField | ModelsListField | MaterialsListField)[];

interface EstimateElementButt {
  thickness: number;
  materialName: string;
  materialArticle: string;
  sign: string;
  sheet: boolean;
}

export interface EstimateElement {
  name: string;
  article: string;
  materialName: string;
  materialArticle: string;
  count: number;
  uids: string;
  butts: EstimateElementButt[];
}

export interface AdditionalParamsData {
  plannerUid?: string;
  plannerProjectId?: string;
}

export interface ConfigurationInfo {
  productName: string;
  baseModelUid: string;
  operationsOrder: number[];
  fields: FieldsData;
  estimateElements?: EstimateElement[];
  additionalParams?: AdditionalParamsData;
}

export interface PresetInfo {
  name: string;
  preview: string;
}

@Injectable({
  providedIn: "root",
})
export class ConfiguratorService {
  constructor(
    private catalogs: CatalogService,
    private files: FilesService,
    private http: HttpClient
  ) {}

  private modelUidName = "bmuidmpl";
  private materialUidName = "uid";

  private ds: BuilderDesigner;

  async applyNewParams(
    newDs: BuilderDesigner,
    operationsOrder: number[],
    fieldsData: FieldsData,
    manualUpdate: boolean = false
  ) {
    this.ds = newDs;

    for (let index of operationsOrder) {
      this.ds.processing.next(true);

      let item = fieldsData.find((item) => item.id === index);
      if (!item) continue;

      if (item.type === FieldType.Number) {
        this.changeDimensions(item as NumberField);
      } else if (item.type === FieldType.ModelsList) {
        await this.replaceModel(item as ModelsListField);
      } else if (item.type === FieldType.MaterialsList) {
        const types = item.modelType.split("|");
        for (let type of types) {
          await this.replaceMaterial(item as MaterialsListField, type);
        }
      }

      if (manualUpdate) {
        await this.manualUpdate();
      }
    }
    this.ds.processing.next(false);
  }

  private async manualUpdate() {
    let syncData = await this.ds.syncModel();
    let buffer = decodeBase64(syncData);
    this.ds.applySyncData(buffer);
  }

  async createNewModel(baseUid: string) {
    const model = await this.files
      .findModelByAttribute(this.modelUidName, baseUid)
      .toPromise();

    const newModel = await this.files
      .copyModel("Новая конфигурация", {
        flushModelId: model.id.toString(),
        type: "load",
        file: model.id,
      })
      .toPromise();

    return newModel.id;
  }

  private getEntitysByType(type: string) {
    const result = [];
    for (let key of Object.keys(this.ds.entityMap)) {
      const entity = this.ds.entityMap[key];

      if (entity.type.trim() === "") continue;

      if (entity.type === type) {
        result.push(entity);
      }
    }
    return result;
  }

  private changeDimensions(field: NumberField) {
    const entity = this.ds.root;
    const axis = `#${field.paramChanged}`;
    const newSize = {};
    newSize[axis] = MathCalculator.calculateRange(field.value, 1);

    this.apply("Resize model by " + axis, [{ uid: entity, size: newSize }]);
  }

  private async apply(name: string, items: BuilderApplyItem[]) {
    await this.ds.applyBatch(name, items);

    this.ds.animateCamera();
    this.ds.zoomToFit();
  }

  private async replaceModel(field: ModelsListField) {
    if (!field.value) return;

    try {
      const srcEntitys: Entity[] = this.getEntitysByType(field.modelType);
      const newFileUid =
        field.allValues.find((item) => item.name === field.value).modelUid ||
        null;

      if (!newFileUid) {
        srcEntitys.forEach((item) => (item.visible = false));
        return;
      }

      const newFileInfo = await this.files
        .findModelByAttribute(this.modelUidName, newFileUid)
        .toPromise();

      if (!srcEntitys.length) return;

      const items: BuilderApplyItem[] = srcEntitys.map((item) => {
        return {
          uid: item,
          replace: {
            insertModelId: newFileInfo.id,
            modelName: newFileInfo.name,
            sku: newFileInfo.sku,
          },
        };
      });

      await this.apply("Replace model", items);
    } catch (err) {
      if (err.error) {
        console.log(err.error);
      } else {
        console.log(err);
      }
    }
  }

  private async replaceMaterial(field: MaterialsListField, type: string) {
    if (!field.value) return;

    try {
      const newMaterialUid = field.allValues.find(
        (item) => item.name === field.value
      ).materialUid;

      const newMatInfo = await this.catalogs
        .findMaterialByAttribute(this.materialUidName, newMaterialUid)
        .toPromise();

      const srcEntitys: Entity[] = this.getEntitysByType(type);

      if (!srcEntitys.length) return;

      const paintInfo = {
        material: newMatInfo.name,
        catalog: newMatInfo.catalogId,
        faces: [],
      };

      const items: BuilderApplyItem[] = this.getItemsForMaterialReplace(
        srcEntitys,
        paintInfo
      );

      await this.apply("Painting", items);
    } catch (err) {
      if (err.error) {
        console.log(err.error);
      } else {
        console.log(err);
      }
    }
  }

  private getEntityReplacableItems(
    srcEntity: Entity,
    info: any,
    items: BuilderApplyItem[] = []
  ) {
    if (!srcEntity.children.length) {
      return [
        {
          uid: srcEntity,
          ...info,
        },
      ];
    }

    const newItems: BuilderApplyItem[] = [];
    srcEntity.children.forEach((child) => {
      let item = items.find((i) => i.uid === child.uid);
      if (!item && child.type.trim() === "") {
        item = {
          uid: child,
          ...info,
        };
        newItems.push(item);
      }
    });

    return newItems;
  }

  private getItemsForMaterialReplace(entitys: Entity[], paintInfo) {
    const replaceItems: BuilderApplyItem[] = [];
    entitys.forEach((entity) => {
      const newItems: BuilderApplyItem[] = this.getEntityReplacableItems(
        entity,
        {
          paint: paintInfo,
        },
        replaceItems
      );

      replaceItems.push(...newItems);
    });

    return replaceItems;
  }

  createConfigFromEntity(
    entity: Entity,
    allMaterials: CatalogMaterial[],
    estimateModel: PriceElement,
    configForEstimate: boolean = false,
    estimate?: EstimateService
  ): ConfigurationInfo {
    let baseModelUid = null;
    if (entity.containsAttribute(this.modelUidName)) {
      baseModelUid = entity.data.attributes[this.modelUidName];
    }

    if (!baseModelUid) {
      return null;
    }

    const fields: FieldsData = [];
    fields.push(
      ...this.getBaseDimensionsConfig(fields.length, entity, configForEstimate)
    );
    fields.push(
      ...this.getModelsConfig(fields.length, entity, configForEstimate)
    );

    if (!configForEstimate) {
      fields.push(
        ...this.getMaterialsConfig(fields.length, entity, allMaterials)
      );
    }

    const operationsOrder = [];
    for (let i = 1; i <= fields.length; i++) {
      operationsOrder.push(i);
    }

    let estimateElements: EstimateElement[] = null;
    if (estimateModel && estimate) {
      estimateElements = [
        ...this.getEstimateElementsConfig(entity, estimateModel, estimate),
      ];
    }

    const additionalParams: AdditionalParamsData = {
      plannerUid: estimateModel.uid,
    };

    return {
      productName: entity.name,
      baseModelUid,
      operationsOrder,
      fields,
      estimateElements,
      additionalParams,
    };
  }

  private getBaseDimensionsConfig(
    lastFieldId: number,
    entity: Entity,
    configForEstimate: boolean
  ): FieldsData {
    const params = {
      width: {
        value: Math.round(entity.sizeBox.sizex),
        localeName: "Ширина",
      },
      height: {
        value: Math.round(entity.sizeBox.sizey),
        localeName: "Высота",
      },
      depth: {
        value: Math.round(entity.sizeBox.sizez),
        localeName: "Глубина",
      },
    };
    const paramNames = Object.keys(params);
    const baseStep = 1;
    const result: FieldsData = [];

    paramNames.forEach((paramName) => {
      lastFieldId++;
      const param = params[paramName];

      let newData: NumberField = {
        id: lastFieldId,
        paramChanged: paramName,
        type: FieldType.Number,
        value: param.value,
      };

      if (!configForEstimate) {
        newData = {
          ...newData,
          min: 0,
          max: param.value,
          name: param.localeName,
          step: baseStep,
        };
      }

      result.push(newData);
    });

    return result;
  }

  private getModelsConfig(
    lastFieldId: number,
    entity: Entity,
    configForEstimate: boolean
  ): FieldsData {
    const result: FieldsData = [];

    entity.forEach((child) => {
      let modelUid = null;
      if (child.containsAttribute(this.modelUidName)) {
        modelUid = child.data.attributes[this.modelUidName];
      }

      if (modelUid && child.type) {
        lastFieldId++;

        let newData: ModelsListField = {
          id: lastFieldId,
          type: FieldType.ModelsList,
          name: child.name,
          modelType: child.type,
        };

        if (configForEstimate) {
          newData = {
            ...newData,
            modelUid,
          };
        } else {
          newData = {
            ...newData,
            value: child.name,
            allValues: [
              {
                name: child.name,
                modelUid,
              },
            ],
          };
        }

        result.push(newData);
      }
    });

    return result;
  }

  private getEntityMaterial(entity: Entity) {
    let material = null;
    if (entity.data.propInfo?.materials.length) {
      material = entity.data.propInfo?.materials[0].new;
    } else if (entity.meshes) {
      material = entity.meshes[0].material;
    } else if (entity.advMaterials) {
      material = entity.advMaterials.split("\n")[0];
    }

    return material;
  }

  private getMaterialsConfig(
    lastFieldId: number,
    entity: Entity,
    allMaterials: CatalogMaterial[]
  ): FieldsData {
    const result: FieldsData = [];
    const map: { [material: string]: string[] } = {};

    entity.forAll((child) => {
      const material = this.getEntityMaterial(child);
      if (material) {
        const needType = child.type ? child.type : child.parent.type;
        const currTypes = map[material];
        if (!currTypes) {
          map[material] = [needType];
        } else {
          const existElem = currTypes.find((item) => item === needType);
          if (!existElem) {
            currTypes.push(needType);
          }
        }
      }
    });

    for (let material of allMaterials) {
      let materialUid = null;
      if (material.attributes && material.attributes[this.materialUidName]) {
        materialUid = material.attributes[this.materialUidName];
      }

      const types = map[material.name];
      if (!materialUid || !types) {
        continue;
      }

      lastFieldId++;
      result.push({
        id: lastFieldId,
        type: FieldType.MaterialsList,
        name: material.name,
        value: material.name,
        modelType: types.join("|"),
        allValues: [
          {
            name: material.name,
            materialUid,
          },
        ],
      });
    }

    return result;
  }

  private getNewEntityButts(entity: Entity, modelProps) {
    const butts: EstimateElementButt[] = [];
    if (modelProps && modelProps.length > 1) {
      for (let index = 1; index < modelProps.length; index++) {
        const prop = modelProps[index];
        const baseMat = prop.params[0].value;
        const [materialName, materialArticle = ""] = prop.valueName.split("\r");

        const buttValue =
          entity.data.attributes && entity.data.attributes[baseMat]
            ? (entity.data.attributes[baseMat] as string)
            : prop.currVariant.values[1];
        let [thickness, sheet, sign = ""] = buttValue.split("\n");
        thickness = thickness.replace(",", ".");

        if (materialName && thickness) {
          butts.push({
            materialName,
            materialArticle,
            thickness: Number(thickness),
            sheet: sheet === "1",
            sign,
          });
        }
      }
    }
    return butts;
  }

  public getEstimateElementsConfig(
    entity: Entity,
    estimateModel: PriceElement,
    estimate?: EstimateService
  ): EstimateElement[] {
    const result: EstimateElement[] = [];
    if (!estimateModel || !entity.isVisible) {
      return result;
    }

    estimateModel.elements.forEach((elem) => {
      const uids: number[] = [];
      const modelProps = estimate.getElementVariantProperties(elem);
      const [elemName, elemArticle = ""] = elem.name.split("\r");
      const [material, materialArticle = ""] = modelProps.length
        ? modelProps[0].valueName.split("\r")
        : [elemName, elemArticle];
      let butts: EstimateElementButt[] = this.getNewEntityButts(
        elem.entities[0],
        modelProps
      );

      elem.entities.forEach((ent) => {
        if (ent.isVisible && ent.data.model?.b3dId) {
          uids.push(ent.data.model?.b3dId);
        }
      });

      if (uids.length > 0) {
        result.push({
          name: elemName,
          article: elemArticle,
          materialName: material,
          materialArticle,
          count: elem.count,
          uids: uids.join(";"),
          butts,
        });
      }
    });

    return result;
  }

  private getWallBaseData(element, floorRoot, defParams) {
    const wall = floorRoot.findChild(
      (item) => item.data.wall && item.data.wall.id === element.id
    );

    const height = (wall && wall.data.wall.height) || defParams.height;
    const material =
      (wall && wall.meshes.length && wall.meshes[0].material) ||
      defParams.material;
    const length = distance(
      new Vector(element.x1, element.y1),
      new Vector(element.x2, element.y2)
    );

    return {
      id: element.id,
      x1: element.x1,
      y1: element.y1,
      x2: element.x2,
      y2: element.y2,
      length,
      thickness: defParams.thickness,
      height,
      material,
    };
  }

  private getLongestLine(contour) {
    let line = contour.items[0].line;
    contour.items.forEach((item) => {
      if (item.length > line.length) {
        line = item;
      }
    });

    return line.clone();
  }

  private setWallNewParams(
    floorBuilder,
    item,
    lastWall = null,
    isInnerWall = false
  ) {
    const wall = floorBuilder.findWall(item.id);
    const contour = floorBuilder.makeWallContour(wall);
    let p1 = contour.items[contour.items.length - 1].line.p1;
    let p2 = contour.items[1].line.p2;
    let rotationAngle = 0;

    if (isInnerWall) {
      let line0 = this.getLongestLine(contour);
      let normDir = line0.normDir;
      let perp = normDir.perpCCW();

      if (normDir.y < 0 || normDir.x < 0) {
        line0.invert();

        if (normDir.y <= 0) {
          normDir = line0.normDir;
        }
      }

      p1 = line0.p1.clone();
      p2 = line0.p2.clone();

      rotationAngle = Math.atan2(p2.x - p1.x, p2.y - p1.y);

      if (perp.y <= -1) {
        p1.y -= item.thickness;
        p2.y -= item.thickness;
      }

      if (rotationAngle === Math.PI / 2) {
        p1.y += item.thickness;
        p2.y += item.thickness;
      }

      if (perp.x <= -1) {
        p1.x -= item.thickness;
        p2.x -= item.thickness;
      }
    }

    if (item.swapPoints) {
      const p3 = p1;
      p1 = p2;
      p2 = p3;
    }

    if (lastWall && lastWall.x2 !== p1.x && lastWall.y2 !== p1.y) {
      p1.x = lastWall.x2;
      p1.y = lastWall.y2;
    }

    item.x1 = p1.x;
    item.y1 = p1.y;
    item.x2 = p2.x;
    item.y2 = p2.y;
    item.rotationAngle = rotationAngle;

    if (contour.items.length === 4) {
      item.length = distance(
        new Vector(item.x1, item.y1),
        new Vector(item.x2, item.y2)
      );
    }
  }

  public getPlacementConfig(
    ds: WebDesigner,
    estimateModels: PriceElement[],
    cloudMap
  ): any {
    const roomWalls = [];
    const innerWalls = [];
    const floorRoot = ds.root.findChild((item) => !!item.data.floor);

    let floorBuilder = new FloorBuilder(floorRoot);
    floorBuilder.init();

    const buffWalls = [
      ...floorRoot.data.floor.map.elements.map((elem) =>
        this.getWallBaseData(elem, floorRoot, {
          thickness: floorBuilder.wallThicknes,
          height: floorBuilder.wallHeight,
          material: floorBuilder.wallMaterial,
        })
      ),
    ];

    const firstWall = buffWalls[0];
    roomWalls.push(firstWall);
    buffWalls.splice(0, 1);

    let contourIsClosed = false;
    let lastWall = firstWall;
    while (!contourIsClosed) {
      let swapPoints = false;
      const currWallIndex = buffWalls.findIndex((item) => {
        let res = item.x1 === lastWall.x2 && item.y1 === lastWall.y2;

        if (!res) {
          res = item.x2 === lastWall.x2 && item.y2 === lastWall.y2;

          if (res) {
            swapPoints = true;
          }
        }

        return res;
      });

      if (currWallIndex > -1) {
        lastWall = buffWalls[currWallIndex];

        if (swapPoints) {
          const { x1, y1, x2, y2 } = lastWall;
          lastWall = {
            ...lastWall,
            x1: x2,
            y1: y2,
            x2: x1,
            y2: y1,
            swapPoints,
          };
        }

        roomWalls.push(lastWall);
        buffWalls.splice(currWallIndex, 1);

        if (firstWall.x1 === lastWall.x2 && firstWall.y1 === lastWall.y2) {
          contourIsClosed = true;
        }
      } else {
        break;
      }
    }

    innerWalls.push(...buffWalls);

    let roomWallsHeight = floorBuilder.wallHeight;
    lastWall = null;
    roomWalls.forEach((item) => {
      this.setWallNewParams(floorBuilder, item, lastWall);
      lastWall = item;

      if (item.height > roomWallsHeight) {
        roomWallsHeight = item.height;
      }
      delete item.height;
    });

    innerWalls.forEach((item) => {
      this.setWallNewParams(floorBuilder, item, null, true);
    });

    const products = [];
    for (let model of estimateModels) {
      const cloudId = cloudMap[model.uid];

      if (!cloudId) continue;

      const elements = model.elements.map((elem) => {
        return {
          name: elem.name,
          price: elem.fullPrice,
          count: elem.count,
        };
      });

      model.entities.forEach((item) => {
        products.push({
          cloudId,
          name: item.name,
          matrix: item.matrix.join(";"),
          price: model.fullPrice,
          elements,
        });
      });
    }

    const materials = [];
    const allMaterials = ds.usedMaterials();
    for (let mat of allMaterials) {
      const textureIsColor = mat.texture ? mat.texture.length === 7 : false;
      const texture = mat.texture
        ? textureIsColor
          ? mat.texture
          : this.files.getTextureUrl(mat, true)
        : null;

      materials.push({
        name: mat.name,
        sizeX: mat.sizex,
        sizeY: mat.sizey,
        angle: mat.angle,
        offsetX: mat.offsetx,
        offsetY: mat.offsety,
        stretch: mat.stretch,
        reflection: mat.reflection,
        transparency: mat.transparency,
        specular: mat.specular,
        shininess: mat.shininess,
        texture,
        textureIsColor,
      });
    }

    return {
      room: {
        roomWalls,
        innerWalls,
        roomWallsHeight,
        floorMaterial: floorBuilder.floorMaterial,
        ceilingMaterial: floorBuilder.ceilingMaterial,
      },
      products,
      materials,
    };
  }

  // Для тестов интеграции с облаком
  // Создание нового заказа
  async createCloudOrder(config: ConfigurationInfo) {
    const { orderId } = await this.http
      .post<any>("/api/cloud/create-order", null)
      .toPromise();

    const { modelId } = await this.http
      .post<any>(`/api/cloud/add-model-from-configurator/${orderId}`, config)
      .toPromise();

    return {
      modelId,
      orderId,
    };
  }

  // Проверка доступности заказа
  async checkOrderStatus(id: number) {
    return await this.http
      .get<boolean>(`/api/cloud/check-order/${id}`)
      .toPromise();
  }

  // Скачивание модели из облака
  async getCloudCloudModel(id: number) {
    return await this.http
      .get(`/api/cloud/get-model/${id}`, {
        responseType: "blob",
      })
      .toPromise();
  }
}
